import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  NavigateNext as NavigateNextIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { useNotification } from '../../../components/Notification';
import Table from '../../../components/Table';
import { formatDateTimeWithCulture } from '../../../utils/dateUtils';
import backupLogsService from '../../../services/backupLogsService';
import {
  fetchBackupLogs,
  deleteBackupLog,
  bulkDeleteBackupLogs,
  selectBackupLogs,
  selectBackupLogsLoading,
  selectBackupLogsError,
  selectBackupLogsSuccessMessage,
  selectBackupLogsTotal,
  selectBackupLogsPage,
  selectBackupLogsPageSize,
  selectBackupLogsSortColumn,
  selectBackupLogsSortDirection,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  clearError,
  clearSuccessMessage,
} from './slice';

function BackupLogs() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const backupLogs = useSelector(selectBackupLogs);
  const loading = useSelector(selectBackupLogsLoading);
  const error = useSelector(selectBackupLogsError);
  const successMessage = useSelector(selectBackupLogsSuccessMessage);
  const total = useSelector(selectBackupLogsTotal);
  const page = useSelector(selectBackupLogsPage);
  const pageSize = useSelector(selectBackupLogsPageSize);
  const sortColumn = useSelector(selectBackupLogsSortColumn);
  const sortDirection = useSelector(selectBackupLogsSortDirection);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);

  // Fetch backup logs on mount and when filters change
  useEffect(() => {
    dispatch(fetchBackupLogs({ page, pageSize, sortColumn, sortDirection }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification and refetch
  useEffect(() => {
    if (successMessage) {
      showNotification(t(successMessage), 'success');
      dispatch(clearSuccessMessage());
      dispatch(fetchBackupLogs({ page, pageSize, sortColumn, sortDirection }));
    }
  }, [successMessage, dispatch, showNotification, t, page, pageSize, sortColumn, sortDirection]);

  // Format file size (bytes to human-readable format)
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Format time (milliseconds to seconds)
  const formatTime = (ms) => {
    return (ms / 1000).toFixed(2) + ' s';
  };

  // Single delete handlers
  const handleDelete = (row) => {
    setLogToDelete(row);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (logToDelete?.id) {
      dispatch(deleteBackupLog(logToDelete.id));
    }
    setShowDeleteDialog(false);
    setLogToDelete(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setLogToDelete(null);
  };

  // Bulk delete handlers
  const handleBulkDelete = () => {
    setShowBulkDeleteDialog(true);
  };

  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteBackupLogs(selectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Download handler
  const handleDownload = async (row) => {
    try {
      await backupLogsService.downloadBackupLog(row.id);
    } catch (err) {
      showNotification(err?.message || t('backupLogs.downloadError'), 'error');
    }
  };

  // Custom actions renderer with download + delete buttons
  const renderActions = (row) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {row.fileName && (
        <IconButton
          onClick={() => handleDownload(row)}
          title={t('backupLogs.download')}
          color="primary"
          size="small"
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        onClick={() => handleDelete(row)}
        title={t('backupLogs.deleteDialog.confirm')}
        color="error"
        size="small"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  const columns = [
    {
      key: 'createdOn',
      label: t('backupLogs.table.createdOn'),
      render: (value) => formatDateTimeWithCulture(value, i18n.language) || '-',
    },
    { key: 'licenseCode', label: t('backupLogs.table.licenseCode') },
    { key: 'unitName', label: t('backupLogs.table.unitName') },
    { key: 'unitCode', label: t('backupLogs.table.unitCode') },
    {
      key: 'fileSize',
      label: t('backupLogs.table.fileSize'),
      render: (value) => formatFileSize(value),
    },
    {
      key: 'totalTime',
      label: t('backupLogs.table.totalTime'),
      render: (value) => formatTime(value),
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header Section */}
      <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon fontSize="small" />
            <Typography variant="body2">{t('backupLogs.breadcrumb.reports')}</Typography>
          </Box>
          <Typography variant="body2" color="text.primary">
            {t('backupLogs.breadcrumb.backupLogs')}
          </Typography>
        </Breadcrumbs>

        {/* Page Title + Bulk Delete Button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
            {t('backupLogs.title')}
          </Typography>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
            disabled={selectedIds.length === 0}
            id="deleteSelected"
            data-testid="backupLogs-button-deleteSelected"
          >
            {t('backupLogs.deleteSelected')} {selectedIds.length > 0 && `(${selectedIds.length})`}
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}>
        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff' }}>
          <Table
            id="backup-logs-table"
            columns={columns}
            data={backupLogs}
            selectable={true}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            loading={loading}
            emptyMessage={t('backupLogs.noData')}
            idField="id"
            renderActions={renderActions}
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={(newPage) => dispatch(setPage(newPage))}
            onPageSizeChange={(newSize) => dispatch(setPageSize(newSize))}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={(col, dir) => {
              dispatch(setSortColumn(col));
              dispatch(setSortDirection(dir));
            }}
          />
        </Box>
      </Paper>

      {/* Confirm Single Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('backupLogs.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('backupLogs.deleteDialog.message', {
              unitName: logToDelete?.unitName || '',
              createdOn: logToDelete?.createdOn ? formatDateTimeWithCulture(logToDelete.createdOn, i18n.language) : '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            data-testid="backupLogs-button-cancelDelete"
          >
            {t('backupLogs.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            data-testid="backupLogs-button-confirmDelete"
          >
            {t('backupLogs.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('backupLogs.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('backupLogs.bulkDeleteDialog.message', { count: selectedIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            data-testid="backupLogs-button-cancelBulkDelete"
          >
            {t('backupLogs.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            data-testid="backupLogs-button-confirmBulkDelete"
          >
            {t('backupLogs.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BackupLogs;
